import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateConfigService } from 'src/app/services/translate-config.service';
import { UserService } from '../../services/user/user.service';
import { IonicModule, SegmentChangeEventDetail, ToastController } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { DetailsHeaderComponent } from '../details-header/details-header.component';
import { DialogComponent } from '../dialog/dialog.component';

@Component({
  selector: 'app-details-mobile',
  templateUrl: './details-mobile.component.html',
  styleUrls: ['./details-mobile.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule, DetailsHeaderComponent, DialogComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class DetailsMobileComponent implements OnInit, AfterViewInit {
  private readonly fallbackMediaImages = [{ image_url: 'assets/images/image-holder.png' }];

  @Input() post:any
  @Input() mode:any
  @Input() guest:any
  @Input() isWeb:boolean = false
  @Output() toggleGuestModal = new EventEmitter();
  @Output() toggleModal = new EventEmitter();
  @ViewChild('activeSegmentPanel') set activeSegmentPanelRef(value: ElementRef<HTMLDivElement> | undefined) {
    this.segmentPanelRef = value;
    this.updateActiveSegmentPanelMinHeight();
  }

  showContactRequestDialog = false;
  showImageViewer = false;
  compactViewport = false;
  activeSegment: 'first' | 'second' = 'first';
  activeSegmentPanelMinHeight = 0;
  selectedImageIndex = 0;
  private readonly compactViewportMaxHeight = 700;
  private segmentPanelRef?: ElementRef<HTMLDivElement>;

  constructor(
    private router: Router,
    private translateService: TranslateConfigService,
    private userService: UserService,
    private toastController: ToastController,
    private translate: TranslateService
  ) { }

  userModal(){
    this.toggleGuestModal.emit()
  }

  toggleShow(){
    this.toggleModal.emit()
  }

  navigate(path: string){
    this.router.navigate([path])
  }

  getMediaImages(): Array<{ image_url: string }> {
    return this.post?.images?.length ? this.post.images : this.fallbackMediaImages;
  }

  openImageViewer(imageIndex: number): void {
    const images = this.getMediaImages();
    const maxIndex = Math.max(images.length - 1, 0);

    this.selectedImageIndex = Math.min(Math.max(imageIndex, 0), maxIndex);
    this.showImageViewer = true;
  }

  closeImageViewer(): void {
    this.showImageViewer = false;
  }

  ngOnInit() {
    this.updateViewportMode();
  }

  ngAfterViewInit(): void {
    this.updateActiveSegmentPanelMinHeight();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewportMode();
    this.updateActiveSegmentPanelMinHeight();
  }

  @HostListener('document:keydown.escape')
  onEscapePressed(): void {
    if (this.showImageViewer) {
      this.closeImageViewer();
    }
  }

  updateViewportMode(viewportHeight: number = window.innerHeight): void {
    this.compactViewport = viewportHeight <= this.compactViewportMaxHeight;
  }

  onSegmentChange(event: CustomEvent<SegmentChangeEventDetail>): void {
    this.activeSegment = event.detail?.value === 'second' ? 'second' : 'first';
    this.updateActiveSegmentPanelMinHeight();
  }

  private updateActiveSegmentPanelMinHeight(): void {
    requestAnimationFrame(() => {
      const segmentPanel = this.segmentPanelRef?.nativeElement;

      if (!segmentPanel) {
        this.activeSegmentPanelMinHeight = 0;
        return;
      }

      this.activeSegmentPanelMinHeight = segmentPanel.scrollHeight;
    });
  }

  isOwner(): boolean {
    const current = this.userService.getUser();
    return !!this.post?.user?.id && !!current?.id && this.post.user.id === current.id;
  }

  /** Opens the contact request dialog (after checking ownership). */
  async openContactRequestDialog(): Promise<void> {
    if (this.isOwner()) {
      const msg = this.translate.instant('DETAILS_MOBILE.CANNOT_CONTACT_OWN');
      const toast = await this.toastController.create({
        message: msg || 'No puedes contactar a tu propia publicación',
        duration: 3000,
        color: 'warning',
        position: 'bottom'
      });
      toast.present();
      return;
    }
    // Found objects skip the intention explanation dialog
    if (this.post?.type === 'found') {
      this.confirmContactRequest();
      return;
    }
    this.showContactRequestDialog = true;
  }

  cancelContactRequestDialog(): void {
    this.showContactRequestDialog = false;
  }

  confirmContactRequest(): void {
    this.showContactRequestDialog = false;
    this.toggleShow();
  }

  /** Returns the i18n key for the contact button based on the post type. */
  getContactButtonKey(): string {
    return this.post?.type === 'found'
      ? 'DETAILS_MOBILE.CONTACT_FOUND_BUTTON'
      : 'DETAILS_MOBILE.CONTACT_LOST_BUTTON';
  }

  /** Returns the i18n key for the informational text based on the post type. */
  getInfoTextKey(): string {
    return this.post?.type === 'found'
      ? 'DETAILS_MOBILE.FOUND_INFO_TEXT'
      : 'DETAILS_MOBILE.LOST_INFO_TEXT';
  }

  /** Returns the i18n key for the contact dialog body based on the post type. */
  getContactDialogBodyKey(): string {
    return this.post?.type === 'found'
      ? 'DETAILS_MOBILE.FOUND_INFO_TEXT'
      : 'DIALOG.NOTIFY_OWNER_BODY';
  }
}
